import _ from "lodash";
import {
  CANVAS_IDS,
  KONVA_ATTR,
  KONVA_EVENTS,
  KONVA_ITEM_TYPE,
} from "./KonvaConstants";
import {
  isTextNode,
  isImageNode,
  isEllipseNode,
  isPolygonNode,
  isRectangleNode,
  createTextNode,
  createImageNode,
  createEllipseNode,
  createPolygonNode,
  createRectangleNode,
  createLabelNode,
} from "./KonvaElements";
import { setCanvasSize } from "../KonvaCommonUtils";

/**
 *
 * @param {*} items
 * @param {*} layer
 * @description add all canvas items to konva layer
 */
export const addElementsToCanvas = (items, props, t) => {
  const { layer, stage, transformer } = props;
  if (!layer || !stage) return;

  // * clear shapes if already exist on canvas
  layer.destroyChildren();
  if (transformer) transformer.detach();

  const canvasHeight = stage.height();
  const canvasWidth = stage.width();
  setCanvasSize({ height: canvasHeight, width: canvasWidth });
  if (!canvasHeight || !canvasWidth) return;

  _.forEach(items, item => {
    switch (_.get(item, "type")) {
      case KONVA_ITEM_TYPE.TEXT:
        return createTextNode(item, props);
      case KONVA_ITEM_TYPE.IMAGE:
        return createImageNode(item, props);
      case KONVA_ITEM_TYPE.ELLIPSE:
        return createEllipseNode(item, props);
      case KONVA_ITEM_TYPE.POLYGON:
        return createPolygonNode(item, props);
      case KONVA_ITEM_TYPE.RECTANGLE:
        return createRectangleNode(item, props);
      case KONVA_ITEM_TYPE.LABEL:
        return createLabelNode(item, props, t);
      default:
        return null;
    }
  });

  layer.draw();
};

/**
 *
 * @param {*} transformer
 * @param {*} node
 * @description remove center handles for text and images to maintain aspect ratio
 */
export const updateTransformerHandler = (node, props = {}) => {
  if (!node || !_.get(props, "transformer")) return;
  const { konva, transformer, virtualLayer, stage } = props;

  transformer.setAttrs({
    anchorStyleFunc: anchor => {
      const isImageOrText = isTextNode(node, konva) || isImageNode(node, konva);

      anchor.cornerRadius(10);
      const isMidAnchor =
        anchor.hasName("top-center") ||
        anchor.hasName("bottom-center") ||
        anchor.hasName("middle-left") ||
        anchor.hasName("middle-right");

      if (isMidAnchor && isImageOrText) {
        // * hiding mid line anchor for image and text
        anchor.height(isMidAnchor && isImageOrText ? 0 : 9);
        anchor.width(isMidAnchor && isImageOrText ? 0 : 9);
      }
    },
  });

  if (node instanceof konva.Shape) {
    virtualLayer.add(transformer);
    transformer.nodes([node]);
    transformer.fire(KONVA_EVENTS.TRANSFORMERATTACHED, { evt: node }, true);
    stage.setAttr(KONVA_ATTR.SELECTED_ITEM_ID, node.id());
    if (_.isFunction(props?.onClick)) props.onClick(node);
  } else {
    const editingTextNodeId = stage.getAttr(KONVA_ATTR.IS_CHANGING_TEXT);
    if (editingTextNodeId) {
      /**
       * if currently text editing mode is one
       * then after blur select text node for continue
       */
      stage.getAttr(KONVA_ATTR.IS_CHANGING_TEXT, undefined);
      const textNode = stage.findOne(`#${editingTextNodeId}`);
      updateTransformerHandler(textNode, props);
    } else {
      transformer.detach();
      transformer.fire(KONVA_EVENTS.TRANSFORMERDETACHED, { evt: node }, true);
      stage.setAttr(KONVA_ATTR.SELECTED_ITEM_ID, undefined);
      if (_.isFunction(props?.onClick)) props.onClick(null);
    }
  }

  virtualLayer.batchDraw();
};

/**
 *
 * @param {*} node
 * @description function maintain uniform scale and change in size while transform
 */
export const maintainTransformationScale = (node, { konva }) => {
  const isText = isTextNode(node, konva);
  const isImage = isImageNode(node, konva);
  const isEllipse = isEllipseNode(node, konva);
  const isPolygon = isPolygonNode(node, konva);
  const isRectangle = isRectangleNode(node, konva);

  const ONE_SCALE = { scaleX: 1, scaleY: 1 };
  const attrs = {};

  if (isRectangle || isImage) {
    /**
     ** Rectangle and Image : scale to width
     */
    const width = node.width() * node.scaleX();
    const height = node.height() * node.scaleY();

    _.set(attrs, "width", Math.max(width, 30));
    _.set(attrs, "height", Math.max(height, 39));
  } else if (isEllipse) {
    /**
     ** Ellipse and Circle : scale to radius
     */
    const radiusX = node.radiusX() * node.scaleX();
    const radiusY = node.radiusY() * node.scaleY();

    _.set(attrs, "radiusX", Math.max(radiusX, 15));
    _.set(attrs, "radiusY", Math.max(radiusY, 15));
  } else if (isPolygon) {
    /**
     ** Polygon : scale to points
     */
    const points = node.points();
    const scaleX = node.scaleX();
    const scaleY = node.scaleY();
    const _points = _.map(
      points,
      (point, index) => point * (index % 2 === 0 ? scaleX : scaleY)
    );

    _.set(attrs, "points", _points);
  } else if (isText) {
    /**
     ** Text size : scale to font-size
     */
    const scale = node.scaleX();
    const fontSize = node.fontSize() * scale;
    const width = node.width() * scale;
    const height = node.height() * scale;
    const padding = fontSize / 2 - fontSize * 0.01;
    node.setAttrs({ fontSize, height, width, padding, ...ONE_SCALE });
    return;
  }

  node.setAttrs({ ...attrs, ...ONE_SCALE });
};

/**
 *
 * @param {*} previous
 * @param {*} data
 * @param {*} mode
 * @description handle initial selection of element on redraw
 */
export const handleSelectionOnRedraw = (previous, data, mode, props) => {
  const stage = _.get(props, "stage");
  if (!stage) return;

  let nodeId = null;
  if (
    _.size(data) > _.size(previous.current?.data) &&
    !_.isEmpty(previous.current?.data)
  ) {
    /**
     * add transformer on new added element
     */
    const diff = _.first(_.differenceBy(data, previous.current?.data, "id"));
    nodeId = _.get(diff, "id");
    previous.current.data = data;
  } else if (_.size(data) < _.size(previous.current?.data)) {
    /**
     * remove transformer on removing element
     */
    previous.current.node = null;
    previous.current.data = data;
  } else if (
    _.get(previous.current, "node") &&
    _.get(previous.current, "mode") === mode
  ) {
    /**
     * Add transformer on last selected element
     */
    const lastSelected = _.get(previous.current, "node");
    nodeId = lastSelected ? lastSelected.id() : null;
  } else {
    previous.current = { mode, data, node: null };
  }

  const node = stage.findOne(`#${nodeId}`);
  previous.current.node = node;
  if (node) updateTransformerHandler(node, props);
};

/**
 *
 * @param {*} konva
 * @description destory entire konva instance
 */
export const destroyKonvaCanvas = konva => {
  if (_.isFunction(_.get(konva, "stage.off"))) {
    konva.stage.off();
  }

  if (_.isFunction(_.get(konva, "virtualLayer.destroyChildren"))) {
    _.forEach(konva.virtualLayer.children, transformer => {
      if (_.isFunction(transformer?.detach)) {
        transformer.off();
        transformer.detach();
        transformer.nodes([]);
        transformer.destroy();
      }
    });
    konva.virtualLayer.destroyChildren();
  }

  if (_.isFunction(_.get(konva, "layer.destroyChildren"))) {
    _.forEach(konva.virtualLayer.children, element => {
      if (element) {
        element.off();
        element.destroy();
      }
    });
    konva.layer.destroyChildren();
    konva.layer.draw();
  }

  return;
};

/**
 *
 * @param {*} type
 * @returns {
 *  isDragAndDrop,
 *  isFindHotspot,
 * }
 */
export const getKonvaQuestionType = type => {
  const isDragAndDrop = _.includes(
    ["dragAndDrop", "dragAndDropResponse"],
    type
  );

  const isFindHotspot = _.includes(
    ["findHotspot", "findHotspotResponse"],
    type
  );

  const isLabelling = _.includes(["labellingTool", "labellingResponse"], type);

  return {
    isDragAndDrop,
    isFindHotspot,
    isLabelling,
  };
};

/**
 *
 * @param {*} konva_instance
 * @description decide amount of element(image/text) can be drag out of canvas
 */
export const addElementOverflowDragBoundary = instance => {
  const stage = _.get(instance, "stage");
  const layer = _.get(instance, "layer");
  if (!layer) return;

  const stageWidth = stage.width();
  const stageHeight = stage.height();

  //  (area * OVERFLOW_PART) amount of area can be out of canvas
  // for future requirement this could be pass as prop
  const OVERFLOW_PART = 0.6;

  _.forEach(_.get(layer, "children", []), _element => {
    let element = _element;

    // * if label group, get label node and create drag boundary for it
    if (_.includes(element.id(), CANVAS_IDS.LABEL_GROUP)) {
      const labelId = _.replace(element.id(), `_${CANVAS_IDS.LABEL_GROUP}`, "");
      const labelNode = stage.findOne(`#${labelId}`);
      if (labelNode) element = labelNode;
    }

    const elementName = _.split(element.name(), "#");
    const type = _.get(elementName, "1");

    if (
      type === KONVA_ITEM_TYPE.IMAGE ||
      type === KONVA_ITEM_TYPE.TEXT ||
      type === KONVA_ITEM_TYPE.LABEL
    ) {
      if (element.draggable()) {
        element.dragBoundFunc(pos => {
          const width = element.width();
          const height = element.height();

          const stageLeft = -(width * OVERFLOW_PART);
          const stageRight = stageWidth + width * OVERFLOW_PART;
          const stageTop = -(height * OVERFLOW_PART);
          const stageBottom = stageHeight + height * OVERFLOW_PART;

          const left = pos.x;
          const right = left + width;
          const top = pos.y;
          const bottom = top + height;

          if (left < stageLeft) pos.x = stageLeft;
          if (right > stageRight) pos.x = stageRight - width;
          if (top < stageTop) pos.y = stageTop;
          if (bottom > stageBottom) pos.y = stageBottom - height;

          return pos;
        });
      }
    }
  });
};
